import React, { useState, useEffect, useRef } from 'react';
import * as Icons from 'lucide-react';
import { Search, ChevronDown } from 'lucide-react';

interface IconSelectProps {
  value: string;
  onChange: (value: string) => void;
}

const IconSelect: React.FC<IconSelectProps> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get all icon names from lucide-react
  const iconNames = Object.keys(Icons).filter(key => 
    key !== 'createLucideIcon' && 
    key !== 'default'
  );

  // Only show icons when search has 2+ characters
  const filteredIcons = search.length >= 2 ? iconNames.filter(name =>
    name.toLowerCase().includes(search.toLowerCase())
  ) : [];

  // Get the selected icon component
  const SelectedIcon = value ? Icons[value as keyof typeof Icons] : null;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white flex items-center gap-2"
      >
        <div className="flex-1 flex items-center gap-2">
          {SelectedIcon && <SelectedIcon className="w-5 h-5" />}
        <span>{value || 'Select an icon'}</span>
        </div>
        <ChevronDown className="w-4 h-4 text-white/40" />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-[#1F2B1F] border border-white/20 rounded-lg shadow-lg">
          <div className="p-2 border-b border-white/10">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search icons..."
                className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white text-sm"
              />
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto p-2">
            {search.length < 2 ? (
              <p className="text-center text-white/50 py-4">Type at least 2 characters to search icons</p>
            ) : filteredIcons.length === 0 ? (
              <p className="text-center text-white/50 py-4">No icons found</p>
            ) : (
              <div className="grid grid-cols-6 gap-2">
                {filteredIcons.map((name) => {
                  const Icon = Icons[name as keyof typeof Icons];
                  return (
                  <button 
                    key={name}
                    type="button"
                    onClick={() => {
                      onChange(name);
                      setIsOpen(false);
                      setSearch('');
                    }}
                    className={`p-2 rounded-lg hover:bg-white/10 transition-colors flex items-center justify-center ${
                      value === name ? 'bg-[#E85C0D]/20 text-[#E85C0D]' : ''
                    }`}
                  > 
                    <Icon className="w-5 h-5" />
                  </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default IconSelect;